/* eslint-disable no-mixed-spaces-and-tabs */
import { useConfirm } from "material-ui-confirm";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { updateWorkerSkillLevel } from "../../../../export/workers";
import useColumnDivider from "../../../../ts/components/cross-skilling/controller/use-column-divider";
import {
	LOCATIONS,
	WORK_CENTERS,
} from "../../../../ts/components/filters/constants";
import {
  PRIMARY_JOB_ROLES,
  REPORTING_TO,
  SHIFT_PATTERNS,
  SKILLS,
  WORKER_TYPES,
} from "../../../../ts/components/filters/constants";
import { FilterContext } from "../../../../ts/components/filters/filter-context";
import { formatDate, hasPermission } from "../../../../ts/utils/helpers";
import { visibleMatrixColumns } from "../../../../ts/utils/matrix-columns";
import useColumnSelector from "../../../design/column-selector/controllers/use-column-selector";
import {
	addSkillInterested,
	fetchManageWorkersData,
	fetchNotAssignedSkillLevel,
	resetSkillLevelStatus,
} from "../../../features/ManageWorkers/ManageWorkersSlice";
import { getLowestLevelObject } from "../../../utils/helpers";
import useViewport from "../../../utils/useViewport";
import useCrossSkillColumn from "./use-cross-skill-column";
import useCrossSkillData from "./use-cross-skill-data";

const useCrossSkillController = () => {
	const {
		allWorkers,
		allJobRoles,
		fetchSkillLevelsStatus,
		allJobs,
		fetchJobsByUserIdStatus,
		workerTypes,
		fetchManageWorkersDataStatus,
		allSkills,
		allSkillLevels,
		allLocations,
		allWorkCenters,
		allShiftPatterns,
		updateWorkerSkillLevelStatus,
		fullTimeSchedule,
		allOrganizations,
		allOrganizationTypes,
		allManagers,
		skillPriorities,
		fetchAllOrganizationsStatus,
		fetchAllLocationsStatus,
		fetchAllManagersStatus,
		skillMatrixFiltersStatus,
		skillMatrixDefaultFiltersStatus,
		skillMatrixAppliedFilters,
		skillInsightsEnabled,
		isSkillPriorityEnabled,
		companyId,
		userRolesWithPermission,
		notAssignedSkillLevel,
		skillMatrixFiltersWorkerSubTypes,
		isWorkerSubTypeEnabled,
    skillMatrixFiltersFilterRelationships,
	} = useCrossSkillData();

	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { width } = useViewport();
	const containerRef = useRef();

	const filterContext = useContext(FilterContext);
	const appliedFilters = useMemo(() => filterContext?.appliedFilters || [], [filterContext?.appliedFilters]);

	const [allData, setAllData] = useState([]);
	const [data, setData] = useState([]);
	const [dataProcessed, setDataProcessed] = useState(false);
	const [filters, setFilters] = useState([]);
	const [filterStatus, setFilterStatus] = useState(false);
	const [selectedWorkCenter, setSelectedWorkCenter] = useState([]);
	const [gotOrgs, setGotOrgs] = useState(false);
	const [totalWorkers, setTotalWorkers] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [selectedPage, setSelectedPage] = useState(0);

	const [searchText, setSearchText] = useState("");
	const [fixedColumns, setFixedColumns] = useState(visibleMatrixColumns([
		"companyEmployeeId",
		"jobTitle",
		"name",
		"workerType",
		"workerSubTypeText",
		"primaryJob",
		"location",
    "skillsCoverage",
		"joiningDate",
		"managerName",
		"workCenter",
		"shiftPatternName",
		"skillScore",
	]));
	const defaultCheckedColumns = visibleMatrixColumns(["skillsCoverage", "skillScore", "name"]);
	const [finalPickerColumn, setFinalPickerColumn] = useState();

	const confirm = useConfirm();
	const filteredJobs = useMemo(() => {
		const jobFilter = appliedFilters.find((f) => f.key === "jobs");
		const skillFilter = appliedFilters.find((f) => f.key === "skills");
		const jobIds = jobFilter?.data.map((d) => d.id) || [];
		// Extract only parent skill IDs (not composite skill-level IDs)
		const skillIds = (skillFilter?.data || [])
			.filter((d) => !d.parentId)
			.map((d) => d.id);

		let jobs = allJobs ?? [];

		// Filter by selected job roles
		if (jobIds.length > 0) {
			jobs = jobs.filter((job) => jobIds.includes(job.id));
		}

		// Filter skills within jobs when skill filter is applied
		if (skillIds.length > 0) {
			jobs = jobs
				.map((job) => ({
					...job,
					skills: job.skills?.filter((skill) => skillIds.includes(skill.skillId || skill.id)) || [],
				}))
				.filter((job) => job.skills.length > 0);
		}

		return jobs;
	}, [allJobs, appliedFilters]);

	const handleSkillLevels = (params, skill, level, setState) => {
		const obj = {
			skillId: skill.id,
			levelId: level.levelId,
			workerId: params.id,
		};

		const workerSkill = params.row.skills.find(
			(x) => parseInt(x.skillId) === parseInt(skill.id)
		);
		if (workerSkill) {
			if (workerSkill.levelId !== level.levelId) {
				confirm({
					title: t("cross-skilling_update-skill-level-for-worker"),
					description: t("cross-skilling_skill-level-will-be-updated", {
						name: params.row.firstName,
						skill: workerSkill.skillName,
						level: level.name,
					}),
					confirmationText: t("cross-skilling_update-uppercase"),
					cancellationText: t("cross-skilling_cancel-uppercase"),
				})
					.then(() => {
						dispatch(updateWorkerSkillLevel(obj));
						setState((state) => ({
							...state,
							level: level.level,
							levelId: level.levelId,
							repCode: level.repCode,
							backColorCode: level.backColorCode,
							foreColorCode: level.foreColorCode,
							value: level.value,
						}));
					})
					.catch(() => {
						/* ... */
					});
			}
		} else {
			confirm({
				title: t("cross-skilling_update-skill-level-for-worker"),
				description: t("cross-skilling_skill-level-will-be-updated", {
					name: params.row.firstName,
					skill: skill.name,
					level: level.name,
				}),
				confirmationText: t("cross-skilling_update-uppercase"),
				cancellationText: t("cross-skilling_cancel-uppercase"),
			})
				.then(() => {
					dispatch(updateWorkerSkillLevel(obj));
					setState((state) => ({
						...state,
						level: level.level,
						levelId: level.levelId,
						repCode: level.repCode,
						backColorCode: level.backColorCode,
						foreColorCode: level.foreColorCode,
						value: level.value,
					}));
				})
				.catch(() => {
					/* ... */
				});
		}
	};
	const handleAddSkillInterested = (params, skill, setState) => {
		if (
			skill &&
			hasPermission(userRolesWithPermission, "CROSS_SKILLING.ACCEPT_INTEREST")
		) {
			const defaultSkillLevel = getLowestLevelObject(allSkillLevels);
			const interestedSkill = params.row.skillInterests
				? params.row.skillInterests.find(
						(x) => parseInt(x.skillId) === parseInt(skill.skillId)
				  )
				: false;
			confirm({
				title: t("cross-skilling_update-skill-for-worker"),
				description: t("cross-skilling_adding-skill-to-worker", {
					skill: skill.name,
					name: params.row.firstName,
				}),
				confirmationText: t("cross-skilling_update-uppercase"),
				cancellationText: t("cross-skilling_cancel-uppercase"),
			})
				.then(() => {
					dispatch(addSkillInterested({ id: interestedSkill.id }));
					setState((state) => ({
						...state,
						level: defaultSkillLevel.level,
						levelId: defaultSkillLevel.levelId,
						repCode: defaultSkillLevel.repCode,
						backColorCode: defaultSkillLevel.backColorCode,
						foreColorCode: defaultSkillLevel.foreColorCode,
						value: defaultSkillLevel.value,
						isInterest: false,
						skillAssigned: true,
					}));
				})
				.catch(() => {
					/* ... */
				});
		}
	};

	const getFilter = useCallback(
		(type) => {
			let returnValue = {
				type: type,
			};
			returnValue[`${type}s`] = [];
			filters.forEach((filter) => {
				if (type === filter.type) {
					returnValue = filter;
				}
			});
			return returnValue;
		},
		[filters]
	);

	const { columns } = useCrossSkillColumn(
		filteredJobs,
		allSkills,
		allSkillLevels,
		handleSkillLevels,
		handleAddSkillInterested,
		searchText,
		appliedFilters,
		isWorkerSubTypeEnabled
	);
	let { defaultColumns, restColumns } = useColumnDivider(
		columns,
		fixedColumns,
		defaultCheckedColumns
	);
	let [{ selectedColumns, pickerColumns }, { handleSelectColumns }] =
		useColumnSelector(defaultColumns, restColumns);

	const filterColumns = useCallback(
		(columns, fixedSet, isEnabled) =>
			columns.filter(
				(col) =>
					fixedSet.has(col.field) &&
					(isEnabled || col.field !== "workerSubTypeText")
			),
		[]
	);

	useEffect(() => {
		const fixedSet = new Set(fixedColumns);
		const filteredColumns = filterColumns(
			pickerColumns,
			fixedSet,
			isWorkerSubTypeEnabled
		);
		setFinalPickerColumn(filteredColumns);
	}, [fixedColumns, pickerColumns, isWorkerSubTypeEnabled, filterColumns]);

	useEffect(() => {
		try {
			if (!allWorkCenters.length || !skillMatrixAppliedFilters.workcenters) {
				setSelectedWorkCenter([]);
				return;
			}

			const appliedWorkCenterIds = new Set(
				skillMatrixAppliedFilters.workcenters
					.split(",")
					.map((id) => parseInt(id))
			);

			const newSelectedWorkCenters = allWorkCenters
				.filter((wc) => appliedWorkCenterIds.has(wc.id))
				.map((wc) => wc.id);

			setSelectedWorkCenter(newSelectedWorkCenters);
		} catch (error) {
			console.error("Error processing workcenters:", error);
			setSelectedWorkCenter([]);
		}
	}, [allWorkCenters, skillMatrixAppliedFilters.workcenters]);

	useEffect(() => {
		dispatch(fetchManageWorkersData({ getAll: true, version: 1 }));
	}, [dispatch]);

	useEffect(() => {
		setDataProcessed(false);
		dispatch(fetchNotAssignedSkillLevel());
	}, [dispatch]);

	const memoizedHelpers = useMemo(
		() => ({
			getSkillName: (skillId) =>
				allSkills.find((x) => parseInt(x.id) === parseInt(skillId))?.name ?? "",

			getJobNames: (jobIds) => {
				const jobIdsArray = jobIds?.split(",") ?? [];
				return jobIdsArray.reduce(
					(names, jobId) => names + (allJobRoles[jobId]?.name ?? ""),
					""
				);
			},

			getLocationName: (locationId) =>
				allLocations.find((x) => x.id === parseInt(locationId))?.name ?? "-",

			getWorkerSubTypeText: (workerSubTypeId) =>
				skillMatrixFiltersWorkerSubTypes.find(
					(type) => type.id === workerSubTypeId
				)?.name ?? "-",

			getWorkCenterName: (workCenterId) =>
				allWorkCenters.find((x) => x.id === parseInt(workCenterId))?.name ??
				"-",

			getManagerName: (managerId) =>
				allManagers.find((x) => x.id === parseInt(managerId))?.name ?? "-",

			getShiftPatternName: (shiftPatternId) =>
				allShiftPatterns.find((x) => x.id === parseInt(shiftPatternId))?.name ??
				"-",

      getSkillsCoverage: (workerId) => {
        // 1. Get the worker and their Work Center ID
        const worker = allWorkers.find(w => w.id === workerId);
        if (!worker || !worker.workcenterId) return "0%";

        // 2. Get all jobs associated with this Work Center
        const workCenterJobs = allJobs.filter(job =>
          job.workCenterIdList.includes(worker.workcenterId)
        );

        // 3. Get UNIQUE Skill IDs needed for the Work Center (The Denominator)
        // We use a Set to automatically remove duplicates if multiple jobs require the same skill
        const workCenterSkillIds = new Set();
        workCenterJobs.forEach(job => {
          job.skills.forEach(skill => {
            // Assuming job.skills contains objects with an id. If it's just an array of IDs, use skill directly.
            workCenterSkillIds.add(skill.id);
          });
        });

        // Guard clause: Avoid dividing by zero if the Work Center has no skills mapped
        if (workCenterSkillIds.size === 0) return "0%";

        // 4. Get the Worker's Relevant Qualified Skills (The Numerator)
        const qualifiedRelevantSkills = worker.skills?.filter(workerSkill => {
          // Check A: Is the worker qualified? (Based on level)
          const isQualified = allSkillLevels.find(level => level.id === workerSkill.levelId)?.isQualifiedAsSkilled;

          // Check B: Is this skill actually part of the Work Center?
          const isRelevantToWorkCenter = workCenterSkillIds.has(workerSkill.skillId || workerSkill.id);

          return isQualified && isRelevantToWorkCenter;
        });

        const relevantCount = qualifiedRelevantSkills?.length || 0;

        if (relevantCount === 0) return "0%";

        // 5. Calculate Percentage
        return `${((relevantCount / workCenterSkillIds.size) * 100).toFixed(2)}%`;
      }
		}),
		[
      allWorkers,
			allSkills,
      allJobs,
			allJobRoles,
			allLocations,
			skillMatrixFiltersWorkerSubTypes,
			allWorkCenters,
			allManagers,
			allShiftPatterns,
      allSkillLevels,
		]
	);

	const defaultExperienceLevel = useMemo(() => {
		const lowest = getLowestLevelObject(allSkillLevels);
		return allSkillLevels.find((level) => level === 0) ?? lowest;
	}, [allSkillLevels]);

	useEffect(() => {
		if (fetchManageWorkersDataStatus !== "fulfilled") {
			if (
				fetchManageWorkersDataStatus === "error" ||
				skillMatrixFiltersStatus === "error"
			) {
				setDataProcessed(true);
			}
			return;
		}

		try {
			const processedWorkers = allWorkers.map((worker) => {
				// Basic worker info
				const baseWorker = {
					...worker,
					workCenterIds: worker.workcenterId.toString(),
					workCenter: memoizedHelpers.getWorkCenterName(worker.workcenterId),
					managerName: memoizedHelpers.getManagerName(worker.managerId),
					workerType: workerTypes.find((type) => type.id === worker.typeId)
						?.name,
					workerSubTypeText: memoizedHelpers.getWorkerSubTypeText(
						worker.workerSubTypeId
					),
					shiftPatternName: memoizedHelpers.getShiftPatternName(
						worker.shiftPatternId
					),
					companyEmployeeId: worker.companyEmployeeId || "-",
					jobNames: memoizedHelpers.getJobNames(worker.jobIds),
					jobTitle: worker.jobTitle || "-",
					primaryJob: worker.primaryJobId
						? memoizedHelpers.getJobNames(worker.primaryJobId.toString())
						: "-",
					location: memoizedHelpers.getLocationName(worker.locationId),
					joiningDate:
						formatDate({
							dateStr: worker.joiningDate,
							formatType: "NUMERIC_DATE",
							ISOString: false,
						}) ?? "-",
					skills: [],
          skillsCoverage: memoizedHelpers.getSkillsCoverage(worker.id) ?? "0%",
				};

				// Process skills
				const processedSkills = new Map();
				const jobIds = worker.jobIds?.split(",") ?? [];

				// Process existing skills
				worker.skills.forEach((skill) => {
					const levelDetails =
						allSkillLevels.find((level) => level.id === skill.levelId) ??
						defaultExperienceLevel;
					const jobWithSkill = jobIds.find((jobId) => {
						const job = allJobRoles[jobId];
						return job?.skills.some(
							(js) => parseInt(js.skillId) === parseInt(skill.skillId)
						);
					});

					const processedSkill = {
						...skill,
						skillName: memoizedHelpers.getSkillName(skill.skillId),
						jobName: allJobRoles[jobWithSkill]?.name ?? "",
						jobId: jobWithSkill ?? `generated-id-${processedSkills.size}`,
						jobAssigned: true,
						skillAssigned: true,
						interested: false,
						...levelDetails,
					};

					processedSkills.set(skill.skillId, processedSkill);
				});

				baseWorker.skills = Array.from(processedSkills.values());
				return baseWorker;
			});

			const uniqueWorkers = [
				...new Map(processedWorkers.map((w) => [w.id, w])).values(),
			];

			setAllData(uniqueWorkers);
			setFixedColumns(visibleMatrixColumns([
				"companyEmployeeId",
				"jobTitle",
				"name",
				"workerType",
				"workerSubTypeText",
				"primaryJob",
				"managerName",
				"location",
        "skillsCoverage",
				"workCenter",
				"shiftPatternName",
				"joiningDate",
				"skillScore",
			]));
			setDataProcessed(true);
		} catch (e) {
			console.error("Error processing worker data:", e);
			setDataProcessed(true);
		}
	}, [
		fetchManageWorkersDataStatus,
		skillMatrixFiltersStatus,
		memoizedHelpers,
		allWorkers,
		allSkillLevels,
		defaultExperienceLevel,
		workerTypes,
		allJobRoles,
	]);

	const filterWorkerData = useCallback(
		(worker, searchTextLower) => {
			const checks = {
				fullName: `${worker.firstName} ${worker.lastName}`
					.toLowerCase()
					.includes(searchTextLower),
				workerType:
					workerTypes
						.find((type) => type.id === worker.typeId)
						?.name.toLowerCase()
						.includes(searchTextLower) ?? false,
				workerSubTypeText:
					worker.workerSubTypeText?.toLowerCase().includes(searchTextLower) ??
					false,
				companyEmployeeId:
					worker.companyEmployeeId?.toLowerCase().includes(searchTextLower) ??
					false,
				managerName:
					worker.managerName?.toLowerCase().includes(searchTextLower) ?? false,
				workerSkills: worker.skills.some((skill) =>
					skill.skillName.toLowerCase().includes(searchTextLower)
				),
				workerJobs:
					worker.jobNames?.toLowerCase().includes(searchTextLower) ?? false,
				jobTitle:
					worker.jobTitle?.toLowerCase().includes(searchTextLower) ?? false,
				workCenter:
					worker.workCenter?.toLowerCase().includes(searchTextLower) ?? false,
				shiftPattern:
					worker.shiftPatternName?.toLowerCase().includes(searchTextLower) ??
					false,
				primaryJob:
					worker.primaryJob?.toLowerCase().includes(searchTextLower) ?? false,
				location:
					worker.location?.toLowerCase().includes(searchTextLower) ?? false,
				joiningDate:
					worker.joiningDate?.toLowerCase().includes(searchTextLower) ?? false,
        skillsCoverage: worker.skillsCoverage?.toLowerCase().includes(searchTextLower) ?? false,
			};

			return Object.values(checks).some(Boolean);
		},
		[workerTypes]
	);

	const processWorkerSkills = useCallback(
		(worker) => ({
			...worker,
			skills: worker.skills
				.filter(
					(skill, index, array) =>
						array.findIndex(
							(s) => parseInt(s.skillId) === parseInt(skill.skillId)
						) === index
				)
				.filter((skill) => skill.skillAssigned),
		}),
		[]
	);

	const getAllReportees = useCallback((managerIds, managers) => {
		const result = new Set(managerIds);
		const queue = [...managerIds];
		while (queue.length > 0) {
			const currentId = queue.shift();
			managers.forEach((m) => {
				if (m.managerId === currentId && !result.has(m.id)) {
					result.add(m.id);
					queue.push(m.id);
				}
			});
		}
		return Array.from(result);
	}, []);

	useEffect(() => {
		try {
			const searchTextLower = searchText.toLowerCase();
			let processedWorkers = allData.map(processWorkerSkills);

			// Apply filter accordion filters
			if (appliedFilters.length > 0) {
				processedWorkers = processedWorkers.filter((worker) => {
					return appliedFilters.every((filter) => {
						if (!filter?.data?.length) return true;

						switch (filter.type) {
							case LOCATIONS:
								return filter.data.some((loc) => loc?.id === worker.locationId);
							case WORK_CENTERS:
								return filter.data.some((wc) => wc?.id === parseInt(worker.workCenterIds));
							case PRIMARY_JOB_ROLES:
								return filter.data.some((job) => job?.id === worker.primaryJobId);
							case REPORTING_TO: {
								const selectedIds = filter.data.map((m) => m.id);
								const expandedIds = getAllReportees(selectedIds, allManagers);
								return expandedIds.includes(worker.managerId);
							}
							case SHIFT_PATTERNS:
								return filter.data.some((sp) => sp?.id === worker.shiftPatternId);
 						case WORKER_TYPES: {
								const selectedIds = new Set(filter.data.map((item) => Number(item.id)));
								const workerTypeId = Number(worker.typeId);
								const subtypeId = worker.workerSubTypeId ? Number(worker.workerSubTypeId) + 1000 : null;
								const hasSubtypeFilters = Array.from(selectedIds).some((id) => id >= 1000);
								if (selectedIds.has(workerTypeId)) {
									return !hasSubtypeFilters || (subtypeId && selectedIds.has(subtypeId));
								}
								return false;
							}
							case SKILLS: {
								// Separate parent skills from child skill-level selections
								const parentSkills = filter.data.filter((item) => !item.parentId);
								const childLevels = filter.data.filter((item) => item.parentId);

								if (parentSkills.length === 0) return true;

								const parentSkillIds = parentSkills.map((item) => item.id);

								// Build a map of skillId -> selected levelIds
								const skillLevelMap = new Map();
								childLevels.forEach((item) => {
									const skillId = item.parentId;
									const levelId = item.id - skillId * 100000; // Extract original level ID
									if (!skillLevelMap.has(skillId)) {
										skillLevelMap.set(skillId, []);
									}
									skillLevelMap.get(skillId).push(levelId);
								});

								// Worker must have at least one of the selected skills
								return parentSkillIds.some((skillId) => {
									const workerSkill = worker.skills.find(
										(s) => parseInt(s.skillId) === parseInt(skillId)
									);
									if (!workerSkill) return false;

									// If skill levels are selected for this skill, check level match
									const selectedLevels = skillLevelMap.get(skillId);
									if (selectedLevels && selectedLevels.length > 0) {
										return selectedLevels.includes(workerSkill.levelId);
									}
									// No level filter for this skill, just having the skill is enough
									return true;
								});
							}
							default:
								return true;
						}
					});
				});
			}

			// Apply search filter
			const filteredWorkers =
				searchText === ""
					? processedWorkers
					: processedWorkers.filter((worker) =>
							filterWorkerData(worker, searchTextLower)
					  );
			setData(filteredWorkers);
		} catch (error) {
			console.error("Error processing worker data:", error);
		}
	}, [searchText, allData, appliedFilters, allManagers, filterWorkerData, processWorkerSkills, getAllReportees]);

	return [
		{
			data,
			allData,
			filters,
			allJobRoles,
			allJobs,
			allManagers,
			allSkills,
			allSkillLevels,
			allWorkCenters,
			allShiftPatterns,
			allWorkers,
			allOrganizations,
			allOrganizationTypes,
			allLocations,
			workerTypes,
			filterStatus,
			selectedWorkCenter,
			updateWorkerSkillLevelStatus,
			fullTimeSchedule,
			width,
			dataProcessed,
			fetchManageWorkersDataStatus,
			fetchAllLocationsStatus,
			fetchSkillLevelsStatus,
			fetchAllOrganizationsStatus,
			fetchAllManagersStatus,
			fetchJobsByUserIdStatus,
			gotOrgs,
			searchText,
			containerRef,
			selectedColumns,
			columns,
			finalPickerColumn,
			skillMatrixFiltersStatus,
			skillMatrixAppliedFilters,
			totalWorkers,
			pageSize,
			selectedPage,
			skillInsightsEnabled,
			isSkillPriorityEnabled,
			skillPriorities,
			notAssignedSkillLevel,
			filteredJobs,
		},
		{
			setFilters,
			setFilterStatus,
			setSelectedWorkCenter,
			resetSkillLevelStatus,
			dispatch,
			handleSkillLevels,
			handleAddSkillInterested,
			handleSelectColumns,
			setSearchText,
			getFilter,
			setSelectedPage,
			setPageSize,
		},
	];
};

export default useCrossSkillController;
